import { NJHeading, NJText } from "@engie-group/fluid-design-system-react";
import DOMPurify from "dompurify";
import Markdown from "markdown-to-jsx";
import type { ComponentProps, ReactNode } from "react";

type SafeLinkProps = {
  href?: string;
  children?: ReactNode;
};

function isSafeHref(href?: string): boolean {
  if (!href) return false;
  const value = href.trim().toLowerCase();
  return (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("mailto:")
  );
}

function SafeLink({ href, children }: SafeLinkProps) {
  if (!isSafeHref(href)) {
    return <span>{children}</span>;
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

/** Renders assistant/user message content as sanitized markdown (no raw HTML, no `.css`).
 * Legacy parity: `components/chat/MarkdownMessage.tsx` — strip all HTML first (DOMPurify),
 * then parse the remaining plain text as markdown (`markdown-to-jsx`).
 *
 * The `prose` wrapper (Tailwind Typography) restores vertical rhythm and styles tables/lists/
 * code/blockquotes — Tailwind's Preflight otherwise strips all block margins, which collapses
 * the rendered markdown visually. Headings/paragraphs are mapped onto the ENGIE `NJHeading`/
 * `NJText` components so the font/scale stay on-brand; `.prose <tag>` selectors still apply the
 * spacing to the `<h*>`/`<p>` these components render. */
// `markdown-to-jsx` never forwards a `ref`, but `ComponentProps` types one as
// `LegacyRef`, which clashes with the NJ* components' `ForwardedRef`. Omit it.
function Heading1(props: Omit<ComponentProps<"h1">, "ref">) {
  return <NJHeading as="h1" scale="md" {...props} />;
}

function Heading2(props: Omit<ComponentProps<"h2">, "ref">) {
  return <NJHeading as="h2" scale="sm" {...props} />;
}

function Heading3(props: Omit<ComponentProps<"h3">, "ref">) {
  return <NJHeading as="h3" scale="xs" {...props} />;
}

function Paragraph(props: Omit<ComponentProps<"p">, "ref">) {
  return <NJText as="p" scale="sm" {...props} />;
}

export function MarkdownMessage({ content }: { content: string }) {
  const sanitized = DOMPurify.sanitize(content, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });

  return (
    <div className="prose prose-sm max-w-none">
      <Markdown
        options={{
          disableParsingRawHTML: true,
          overrides: {
            a: { component: SafeLink },
            h1: { component: Heading1 },
            h2: { component: Heading2 },
            h3: { component: Heading3 },
            p: { component: Paragraph },
          },
        }}
      >
        {sanitized}
      </Markdown>
    </div>
  );
}
